import React from "react";
import { Box, Button, Flex, IconButton, Portal, Text } from "@chakra-ui/react";
import { InfoIcon } from "@chakra-ui/icons";
import { keyframes } from "@emotion/react";
import TierInfoPopover from "../TierInfoPopover";
import { tt } from "../../i18n/tt";
import { getGuideHint } from "../../lib/guideHints";
import { askLayFromScreen } from "../../lib/layChat";
import { useIsMobileViewport } from "../../hooks/useIsMobileViewport";

// 상시 도움말 — 라벨 옆 ⓘ 하나. 기획 docs/ops/kakao-archive-tooltip-tutorial-plan.md §2 (2026-09-20, 대표 결정 ⓐ).
//
// - 30일짜리 코치마크(SpotlightCoachmark)와 달리 **항상 그 자리에** 있다. 반복 문의(정산이 몇 월분인지, 버튼이 왜 안 눌리는지,
//   배지가 무슨 뜻인지)는 이 한 줄이 답이다.
// - md 이상: 팝오버(기존 ⓘ 팝오버 규격 TierInfoPopover 재사용 — 새 Popover 를 조립하지 않는다). 2026-09-27 부터 그 팝오버는 Portal —
//   홈 왼쪽 필터 패널(sticky + overflowY auto 216px) 안에서 잘리던 문제. 이 컴포넌트의 stop() 은 React 트리 버블링이라 Portal 을 넘어도 그대로 듣는다.
//   base: 바텀시트(SpotlightCoachmark 와 같은 규격). Chakra Tooltip 은 hover 라 휴대폰에서 안 뜬다.
// - 내용: 1~2문장 + [가이드에서 더 보기](새 창) + [레이에게 묻기](질문 미리 채움, `laeebly:lay:ask` 이벤트).
// - 문구는 src/lib/guideHints.js 한 곳. 모르는 id 는 아무것도 그리지 않는다(fail-open).
// - 카드·행 안에 놓여도 부모 onClick 이 따라 열리지 않게 클릭 전파를 막는다.

const Z = 1450; // Chakra 모달(1400) 위, 토스트(5500) 아래 — 모달 안 라벨에서도 시트가 보이게
const rise = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// 부모(카드·행·<label>·링크 카드)로 클릭이 새지 않게 — React 전파를 막고, <label> 활성화·링크 이동 같은 기본 동작도 막는다.
// 단 팝오버 안의 「가이드에서 더 보기」(a 태그)는 기본 동작이 곧 기능이라 그대로 둔다.
function stop(e) {
  e.stopPropagation();
  const t = e.target;
  if (t && typeof t.closest === "function" && t.closest("a")) return;
  e.preventDefault();
}

function HintLinks({ hint, onDone, column = false }) {
  const ask = () => {
    if (hint.askLay) askLayFromScreen(hint.askLay);
    if (onDone) onDone();
  };
  return (
    <Flex gap={column ? 2 : 3} mt={column ? 1 : 2} direction={column ? "column" : "row"} align={column ? "stretch" : "center"} flexWrap="wrap">
      <Button
        as="a"
        href={hint.guideUrl}
        target="_blank"
        rel="noopener noreferrer"
        size={column ? "md" : "xs"}
        variant={column ? "outline" : "link"}
        colorScheme="brand"
        color="brand.700"
        fontWeight="600"
        h={column ? "44px" : undefined}
        borderRadius={column ? "10px" : undefined}
      >
        {tt("a.dfd3c0e8", "가이드에서 더 보기")}
      </Button>
      {hint.askLay && (
        <Button size={column ? "md" : "xs"} variant={column ? "solid" : "link"} colorScheme="brand" fontWeight="600" h={column ? "44px" : undefined} borderRadius={column ? "10px" : undefined} onClick={ask}>
          {tt("a.f9c34d63", "레이에게 묻기")}
        </Button>
      )}
    </Flex>
  );
}

function MobileSheet({ hint, onClose }) {
  const firstRef = React.useRef(null);
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); } };
    document.addEventListener("keydown", onKey, true);
    try { firstRef.current?.focus({ preventScroll: true }); } catch { /* 무시 */ }
    return () => document.removeEventListener("keydown", onKey, true);
  }, [onClose]);
  return (
    <Portal>
      <Box position="fixed" inset={0} bg="rgba(17, 24, 39, 0.6)" zIndex={Z} onClick={(e) => { stop(e); onClose(); }} />
      <Box
        role="dialog"
        aria-modal="true"
        aria-label={hint.title}
        position="fixed"
        left={0}
        right={0}
        bottom={0}
        zIndex={Z + 1}
        bg="white"
        borderRadius="20px 20px 0 0"
        px={5}
        pt={2.5}
        pb="max(20px, env(safe-area-inset-bottom))"
        boxShadow="0 -12px 40px rgba(0,0,0,0.3)"
        onClick={stop}
        sx={{ animation: `${rise} 0.28s cubic-bezier(0.2, 0.8, 0.2, 1) both`, "@media (prefers-reduced-motion: reduce)": { animation: "none" } }}
      >
        <Box w="40px" h="4px" borderRadius="2px" bg="neutral.300" mx="auto" mb={3} />
        <Flex direction="column" gap={3}>
          <Text fontSize="19px" fontWeight="800" lineHeight="1.35" letterSpacing="-0.01em" color="neutral.900" sx={{ wordBreak: "keep-all" }}>{hint.title}</Text>
          <Text fontSize="14px" lineHeight="1.6" color="neutral.700" sx={{ wordBreak: "keep-all" }}>{hint.body}</Text>
          <HintLinks hint={hint} onDone={onClose} column />
          <Button ref={firstRef} variant="ghost" color="neutral.500" h="44px" fontSize="15px" fontWeight="600" borderRadius="8px" onClick={onClose}>
            {tt("a.218e2ae6", "닫기")}
          </Button>
        </Flex>
      </Box>
    </Portal>
  );
}

/**
 * `<GuideHint id="settle.which-month" />` — 라벨 옆에 놓는 ⓘ.
 * @param {{ id: string, tone?: "light"|"dark", placement?: string }} props
 */
export default function GuideHint({ id, tone = "light", placement = "top" }) {
  const hint = getGuideHint(id);
  const isMobile = useIsMobileViewport(); // md(48em) 미만이면 바텀시트 · matchMedia 가 없으면(jsdom) 팝오버
  const [open, setOpen] = React.useState(false);
  const close = React.useCallback(() => setOpen(false), []);
  if (!hint) return null;
  const ariaLabel = tt("a.ee91eb7a", "도움말: {{v0}}", { v0: hint.title });

  if (isMobile) {
    return (
      // pointerEvents="auto" — 아직 잠긴 단계(VideoStepRail 이 pointerEvents none)의 ⓘ 도 눌러서 읽을 수 있게(2026-09-27 최종 검수)
      <Box as="span" display="inline-flex" verticalAlign="middle" onClick={stop} data-guide-hint={id} pointerEvents="auto">
        <IconButton
          aria-label={ariaLabel}
          aria-haspopup="dialog"
          aria-expanded={open}
          icon={<InfoIcon />}
          size="xs"
          variant="ghost"
          isRound
          minW="24px"
          h="24px"
          color={tone === "dark" ? "whiteAlpha.800" : "neutral.400"}
          _hover={{ color: tone === "dark" ? "white" : "neutral.600", bg: tone === "dark" ? "whiteAlpha.300" : "neutral.100" }}
          onClick={() => setOpen(true)}
        />
        {open && <MobileSheet hint={hint} onClose={close} />}
      </Box>
    );
  }

  return (
    <Box as="span" display="inline-flex" verticalAlign="middle" onClick={stop} data-guide-hint={id} pointerEvents="auto">
      <TierInfoPopover ariaLabel={ariaLabel} title={hint.title} placement={placement} tone={tone} maxW="300px">
        <Text as="span" display="block" sx={{ wordBreak: "keep-all" }}>{hint.body}</Text>
        {/* 「레이에게 묻기」는 위젯이 열리며 입력칸으로 포커스가 옮겨가 팝오버가 blur 로 닫힌다 */}
        <HintLinks hint={hint} />
      </TierInfoPopover>
    </Box>
  );
}

/**
 * 라벨 + ⓘ 를 한 줄로 — 호출부를 짧게 유지하고 싶을 때.
 *   <GuideHintLabel id="inspect.url">{tt("…", "유튜브 영상 URL")}</GuideHintLabel>
 */
export function GuideHintLabel({ id, children, gap = 1, ...rest }) {
  return (
    <Box as="span" display="inline-flex" alignItems="center" gap={gap} {...rest}>
      {children}
      <GuideHint id={id} />
    </Box>
  );
}
